import { useCallback, useRef, useState } from 'react';

export const useVoiceFeedback = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const synthRef = useRef(null);
  const lastSpokenRef = useRef(0);
  const MIN_SPEAK_INTERVAL = 1500; // Minimum time between announcements

  // Initialize speech synthesis
  const getSynth = useCallback(() => {
    if (!synthRef.current && typeof window !== 'undefined') {
      synthRef.current = window.speechSynthesis;
    }
    return synthRef.current;
  }, []);

  // Speak a message
  const speak = useCallback((text, priority = false) => {
    if (!isEnabled) return;
    
    const synth = getSynth();
    if (!synth) return;

    const now = Date.now();
    if (!priority && now - lastSpokenRef.current < MIN_SPEAK_INTERVAL) {
      return;
    }

    // Cancel current speech if priority
    if (priority && synth.speaking) {
      synth.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.1;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synth.speak(utterance);
    lastSpokenRef.current = now;
  }, [isEnabled, getSynth]);

  // Announce rep count
  const announceRep = useCallback((count) => {
    speak(String(count));
  }, [speak]);

  // Announce goal achieved
  const announceGoalAchieved = useCallback(() => {
    speak('Goal achieved! Great work!', true);
  }, [speak]);

  // Announce encouragement
  const announceEncouragement = useCallback((repsLeft) => {
    if (repsLeft === 5) {
      speak('Five more to go!', true);
    } else if (repsLeft === 1) {
      speak('Last one!', true);
    }
  }, [speak]);

  // Announce exercise start
  const announceStart = useCallback(() => {
    speak('Exercise started. Begin when ready.', true);
  }, [speak]);

  // Announce exercise end
  const announceEnd = useCallback((reps, goal) => {
    if (reps >= goal) {
      speak(`Excellent! You completed ${reps} reps, meeting your goal!`, true);
    } else {
      speak(`Session complete. ${reps} out of ${goal} reps.`, true);
    }
  }, [speak]);

  // Toggle voice feedback
  const toggleVoice = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  return {
    isEnabled,
    isSpeaking,
    speak,
    announceRep,
    announceGoalAchieved,
    announceEncouragement,
    announceStart,
    announceEnd,
    toggleVoice,
    setIsEnabled
  };
};

export default useVoiceFeedback;
